import { useMemo } from 'react';
import { Badge } from '@react-apps/design-system';
import { PerkAssociationEditor } from '@/modules/catalog/presentation/components/PerkAssociationEditor';
import type {
  ExistingPerkRow,
  PerkAssociationTarget,
} from '@/modules/catalog/presentation/components/PerkAssociationEditor';
import {
  useAllPerkGroups,
  useGrantPolicySpecs,
} from '@/modules/catalog/presentation/hooks/use-perks';
import { indexPerkForms } from '@/modules/catalog/presentation/perk-index';
import { DeprecatedBadge } from '@/modules/catalog/presentation/components/DeprecatedBadge';
import { canGrantPerk } from '@/shared/guards/roles';
import { useGranterPerkKeys } from '@/modules/auth/presentation/hooks/use-granter-perk-keys';
import type {
  EffectivePerk,
  GrantedPerk,
} from '../../domain/entities/user.entity';
import { orderByConsumptionPriority } from '../../domain/consumption-order';
import {
  aggregatedTail,
  derivedPerks,
  findDerived,
  sustainersAfterRevocation,
} from '../../domain/perk-composition';
import {
  useApplyPerkGrantBatch,
  useEffectivePerks,
  useProfilePerks,
} from '../hooks/use-profile-perks';
import { DerivedPerksBlock } from './DerivedPerksBlock';
import { GrantedPerkSummary } from './GrantedPerkSummary';

/** A linha existente aqui é um **pacote concedido**, não um perk do catálogo. */
type GrantedPerkRow = ExistingPerkRow & { grant: GrantedPerk };

/** Identidade estável enquanto o efetivo do perfil não chegou. */
const NO_EFFECTIVE_PERKS: EffectivePerk[] = [];

interface ProfilePerksEditorModalProps {
  profileId: string;
  profileLabel: string;
  /** Scope do perfil: o catálogo da direita só oferece o que vale nele. */
  scopeId: string;
  open: boolean;
  onClose: () => void;
}

/**
 * O alvo **usuário** do editor. Difere dos dois de catálogo em tudo que decorre
 * de conceder não ser associar (§5 do plano): o mesmo perk pode entrar mais de
 * uma vez no lote (soma na janela, pacote paralelo no crédito), a remoção é uma
 * **revogação** com motivo obrigatório, e não existe "editar valores" — um
 * pacote concedido não se edita, se joga fora e se abre outro.
 */
export function ProfilePerksEditorModal({
  profileId,
  profileLabel,
  scopeId,
  open,
  onClose,
}: ProfilePerksEditorModalProps) {
  const { data: granted = [], isLoading } = useProfilePerks(profileId);
  const { data: groups } = useAllPerkGroups();
  const { data: specs } = useGrantPolicySpecs();
  const { mutateAsync } = useApplyPerkGrantBatch(profileId);
  const {
    data: effective = NO_EFFECTIVE_PERKS,
    isLoading: effectiveLoading,
    isError: effectiveError,
  } = useEffectivePerks(profileId, scopeId);

  // Perks do admin LOGADO — decidem o que ele pode oferecer (grantableByPerkKeys).
  const {
    keys: myPerkKeys,
    isLoading: myPerkKeysLoading,
    isError: myPerkKeysError,
  } = useGranterPerkKeys(scopeId);

  const perkIndex = useMemo(() => indexPerkForms(groups), [groups]);

  /**
   * Em que ordem os pacotes ativos **da mesma capacidade** seriam consumidos.
   * Capacidade com um pacote só não disputa nada e não recebe posição.
   */
  const consumptionPositions = useMemo(() => {
    const byCapability = new Map<string, GrantedPerk[]>();
    for (const pkg of granted) {
      if (pkg.status !== 'active') continue;
      const key = `${pkg.perkType}:${pkg.perkKey}`;
      const bucket = byCapability.get(key);
      if (bucket) bucket.push(pkg);
      else byCapability.set(key, [pkg]);
    }
    const positions = new Map<string, number>();
    for (const bucket of byCapability.values()) {
      if (bucket.length < 2) continue;
      orderByConsumptionPriority(bucket, specs).forEach((pkg, index) => {
        positions.set(pkg.id, index + 1);
      });
    }
    return positions;
  }, [granted, specs]);

  /**
   * Marca de **agregador**: o que revogar este pacote derruba junto (§6). Só
   * para pacote que está no efetivo — expirado ou inerte por portão não
   * sustenta nada. A cauda é o efeito: transitiva, e sem o filho que outro
   * pacote também sustenta.
   */
  const tailByGrantId = useMemo(() => {
    const tails = new Map<string, string[]>();
    for (const perk of effective) {
      if (perk.derived || perk.grantedPerkId === null) continue;
      const tail = aggregatedTail(perk.grantedPerkId, effective);
      if (tail.length > 0) {
        tails.set(perk.grantedPerkId, [
          ...new Set(tail.map((child) => child.perkKey)),
        ]);
      }
    }
    return tails;
  }, [effective]);

  const target: PerkAssociationTarget<GrantedPerkRow> = useMemo(
    () => ({
      title: `Vantagens — ${profileLabel}`,
      description:
        'Conceda e revogue os pacotes deste perfil. Nada é gravado até aplicar; o lote inteiro é aceito ou recusado junto.',
      // Conceder o mesmo perk duas vezes é legítimo: soma prazo na janela, abre
      // um pacote paralelo no crédito.
      duplicatesAllowed: true,
      scopeIdToValidate: scopeId,
      // Só oferece o que o admin logado pode conceder (espelha o assertCanGrant
      // do back). Perk que ele não pode dar some da lista em vez de virar 403.
      offerFilter: (form) => canGrantPerk(form.grantableByPerkKeys, myPerkKeys),
      offerLoading: myPerkKeysLoading,
      offerError: myPerkKeysError,
      supportsTargetScopes: false,
      removal: { label: 'Revogar', needsReason: true },
      existing: granted.map((grant) => ({
        id: grant.id,
        perkId: grant.perkId,
        grant,
      })),
      existingLoading: isLoading,
      // Expirado, revogado ou esgotado já não está valendo — revogar não muda
      // nada e o back recusa.
      canRemove: ({ grant }) =>
        grant.status === 'active' || grant.status === 'suspended',
      // Pacote de perk depreciado continua visível e **revogável** (`canRemove`
      // acima não olha depreciação) — só não dá para conceder outro, o que a
      // oferta da direita já barra. O badge explica por que ele sumiu do catálogo.
      renderExisting: ({ grant }) => {
        const resolved = perkIndex.get(grant.perkId);
        return (
          <div className="flex items-center gap-2">
            <GrantedPerkSummary
              pkg={grant}
              position={consumptionPositions.get(grant.id)}
              perkName={resolved?.group.name ?? undefined}
            />
            <DeprecatedBadge
              deprecatedAt={resolved?.form.deprecatedAt ?? null}
            />
            <AggregatorMark tail={tailByGrantId.get(grant.id)} />
          </div>
        );
      },
      existingAppendix: (
        <DerivedPerksBlock
          derived={derivedPerks(effective)}
          perkIndex={perkIndex}
          isLoading={effectiveLoading}
          isError={effectiveError}
        />
      ),
      // Conceder o que já vem por composição é legítimo — dá ao perk uma vida
      // que sobrevive à remoção do pai —, mas quase nunca é o que se quer.
      pendingNotice: (row) => {
        const derived = findDerived(row.perkId, effective);
        if (!derived) return null;
        const parents = [
          ...new Set(derived.derivedFrom.map((p) => p.perkKey)),
        ].join(', ');
        return `Este perfil já tem por composição de ${parents}; conceder abre um pacote próprio.`;
      },
      // Revogar o pacote de algo que outro acesso continua derivando não tira
      // o acesso — é o que faz o operador achar que tirou.
      removalNotice: ({ grant }, removing) => {
        const sustainers = sustainersAfterRevocation({
          perkId: grant.perkId,
          perkKey: grant.perkKey,
          // Só kind de janela é derivável — a `spec` é a lista do back, não
          // uma cópia daqui.
          isWindow:
            specs?.find((spec) => spec.kind === grant.grantPolicyKind)
              ?.isWindow ?? false,
          revokedGrantIds: new Set(removing.map((row) => row.id)),
          effective,
          grantsPerkKeysOf: (perkId) =>
            perkIndex.get(perkId)?.form.grantsPerkKeys ?? [],
        });
        if (sustainers.length === 0) return null;
        return `O acesso não cai: ${grant.perkKey} continua por composição de ${sustainers.join(', ')}.`;
      },
      submit: async ({ add, remove, reason }) => {
        await mutateAsync({
          grant: add.map((addition) => ({
            perkId: addition.perkId,
            grantPolicyParams: addition.grantPolicyParams,
            ...(addition.presetId ? { presetId: addition.presetId } : {}),
          })),
          revoke: remove.map((row) => ({ grantedPerkId: row.id, reason })),
        });
      },
    }),
    [
      profileLabel,
      scopeId,
      granted,
      isLoading,
      consumptionPositions,
      perkIndex,
      mutateAsync,
      myPerkKeys,
      myPerkKeysLoading,
      myPerkKeysError,
      effective,
      effectiveLoading,
      effectiveError,
      tailByGrantId,
      specs,
    ],
  );

  return (
    <PerkAssociationEditor open={open} onClose={onClose} target={target} />
  );
}

/**
 * "Revogar isto derruba mais coisa." Rótulo e cor próprios, que não se confundem
 * com o selo "Por composição" dos derivados: são duas perguntas diferentes.
 */
function AggregatorMark({ tail }: { tail: string[] | undefined }) {
  if (!tail || tail.length === 0) return null;
  return (
    <span className="flex flex-wrap items-center gap-1.5">
      <Badge variant="default">Agregador</Badge>
      <span className="text-(--color-text-secondary) text-xs">
        concede também: <span className="font-mono">{tail.join(', ')}</span>
      </span>
    </span>
  );
}
